import { Check, Pencil, X } from "lucide-react";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "../../../../../components/ui/common/button";
import { Form } from "../../../../../components/ui/common/form";
import { FormInputNumber } from "../../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { FormUnitInput } from "../../../../../components/ui/Input/FormUnitInput/FormUnitInput";
import { WEIGHT_UNIT_TYPES_OPTIONs_WITH_GRAM } from "../../../../../config/optionsConstants";
import { MEASUREMENT_TYPES } from "../../../../../config/enums/MeasurementTypes";
import { PackagingLevelViewModel, PackagingUnitSnapshot } from "../types/packagingDetail.types";
import PackagingTotalWeightBadge from "./PackagingTotalWeightBadge";
import { weightUnitLabel } from "../utils/packagingMaterial.utils";

interface UnitFormValues {
  unitValue: number | "";
  weightUnit: string;
  palletWidth: number | "";
  palletLength: number | "";
}

interface PackagingUnitRowProps {
  level: PackagingLevelViewModel;
  unitLabel: string;
  totalWeightLabel: string;
  isSingleUnit: boolean;
  isTransport: boolean;
  disabled?: boolean;
  onSaveUnit: (patch: Partial<PackagingUnitSnapshot>) => void;
}

/**
 * The level's unit-count row (Unit Size / Number of Base Units / Number of Packs /
 * Number of Cases, plus pallet width/length on transport). Lifted verbatim from
 * `PackagingLevelSection` — behaviour and payload shape are unchanged.
 */
const PackagingUnitRow: React.FC<PackagingUnitRowProps> = ({
  level,
  unitLabel,
  totalWeightLabel,
  isSingleUnit,
  isTransport,
  disabled = false,
  onSaveUnit
}) => {
  const [unitEditing, setUnitEditing] = useState(false);

  const unitForm = useForm<UnitFormValues>({
    defaultValues: { unitValue: "", weightUnit: String(MEASUREMENT_TYPES.WEIGHT_KG), palletWidth: "", palletLength: "" }
  });

  const startUnitEdit = () => {
    unitForm.reset({
      unitValue: level.unitValue || "",
      weightUnit: String(level.unitSizeWeightUnit ?? MEASUREMENT_TYPES.WEIGHT_KG),
      palletWidth: level.palletWidth || "",
      palletLength: level.palletLength || ""
    });
    setUnitEditing(true);
  };

  const saveUnit = unitForm.handleSubmit(data => {
    const patch: Partial<PackagingUnitSnapshot> = { [level.unitFieldKey]: Number(data.unitValue) || 0 };
    if (isSingleUnit) patch.unitSizeWeightUnit = Number(data.weightUnit) || 0;
    if (isTransport) {
      patch.palletWidth = Number(data.palletWidth) || 0;
      patch.palletLength = Number(data.palletLength) || 0;
    }
    onSaveUnit(patch);
    setUnitEditing(false);
  });

  return (
    <Form {...unitForm}>
      <div className="tw-px-3 tw-py-4">
        {isTransport ? (
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-w-1/2 tw-min-w-[420px]">
              <div className="tw-grid tw-grid-cols-3 tw-gap-6">
                {(
                  [
                    ["Number of Cases", "unitValue"],
                    ["Pallet Width", "palletWidth"],
                    ["Pallet Length", "palletLength"]
                  ] as const
                ).map(([label, field]) => (
                  <div key={field} className="[&_label]:tw-hidden [&_.tw-space-y-2]:tw-space-y-0">
                    <div className="tw-text-[10px] tw-font-medium tw-text-muted-foreground tw-uppercase tw-tracking-wide">
                      {label} *
                    </div>
                    {unitEditing ? (
                      <FormInputNumber
                        control={unitForm.control}
                        name={field}
                        label=""
                        className="tw-h-8 tw-w-24 tw-text-xs"
                      />
                    ) : (
                      <div className="tw-text-sm">
                        {(field === "unitValue" ? level.unitValue : level[field]) || "—"}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {unitEditing ? (
                <div className="tw-flex tw-gap-1">
                  <Button variant="ghost" size="icon" className="tw-h-7 tw-w-7 tw-text-primary" onClick={saveUnit} title="Save">
                    <Check className="tw-h-3.5 tw-w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="tw-h-7 tw-w-7"
                    onClick={() => setUnitEditing(false)}
                    title="Cancel"
                  >
                    <X className="tw-h-3.5 tw-w-3.5" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  className="tw-h-7 tw-w-7 tw-text-primary"
                  onClick={startUnitEdit}
                  title="Edit"
                  disabled={disabled}
                >
                  <Pencil className="tw-h-3.5 tw-w-3.5" />
                </Button>
              )}
            </div>
            <PackagingTotalWeightBadge label={totalWeightLabel} value={level.totalWeight} unit="kg" />
          </div>
        ) : (
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-w-1/2 tw-min-w-[220px]">
              <div className="[&_label]:tw-hidden [&_.tw-space-y-2]:tw-space-y-0">
                <div className="tw-text-[10px] tw-font-medium tw-text-muted-foreground tw-uppercase tw-tracking-wide">
                  {unitLabel} *
                </div>
                {unitEditing ? (
                  isSingleUnit ? (
                    <FormUnitInput
                      control={unitForm.control}
                      name="unitValue"
                      label=""
                      unitName="weightUnit"
                      unitOptions={WEIGHT_UNIT_TYPES_OPTIONs_WITH_GRAM}
                    />
                  ) : (
                    <FormInputNumber
                      control={unitForm.control}
                      name="unitValue"
                      label=""
                      className="tw-h-8 tw-w-24 tw-text-xs"
                    />
                  )
                ) : (
                  <div className="tw-text-sm">
                    {level.unitValue
                      ? `${level.unitValue}${isSingleUnit ? ` ${weightUnitLabel(level.unitSizeWeightUnit)}` : ""}`
                      : "—"}
                  </div>
                )}
              </div>
              {unitEditing ? (
                <div className="tw-flex tw-gap-1">
                  <Button variant="ghost" size="icon" className="tw-h-7 tw-w-7 tw-text-primary" onClick={saveUnit} title="Save">
                    <Check className="tw-h-3.5 tw-w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="tw-h-7 tw-w-7"
                    onClick={() => setUnitEditing(false)}
                    title="Cancel"
                  >
                    <X className="tw-h-3.5 tw-w-3.5" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  className="tw-h-7 tw-w-7 tw-text-primary"
                  onClick={startUnitEdit}
                  title="Edit"
                  disabled={disabled}
                >
                  <Pencil className="tw-h-3.5 tw-w-3.5" />
                </Button>
              )}
            </div>
            <PackagingTotalWeightBadge label={totalWeightLabel} value={level.totalWeight} unit="kg" />
          </div>
        )}
      </div>
    </Form>
  );
};

export default PackagingUnitRow;
